import React, { useEffect, useRef } from 'react';
import * as styles from './InPlaceEditable.module.scss';

interface InPlaceEditableProps {
  defaultValue: string;
  placeholder: string;
  onChange(value: string): void;
  autoFocus?: boolean;
}

const DISALLOWED_INPUT_TYPES = ['insertParagraph', 'insertLineBreak'];

const InPlaceEditable = ({
  defaultValue,
  placeholder,
  onChange,
  autoFocus,
}: InPlaceEditableProps) => {
  const defaultValueRef = useRef(defaultValue);
  const editableRef = useRef<HTMLSpanElement>(null);
  const handleChange = () => {
    if (editableRef.current) {
      onChange(editableRef.current.textContent ?? '');
    }
  };

  useEffect(() => {
    const editable = editableRef.current;
    if (editable && autoFocus) {
      editable.focus();
      const selection = document.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      if (range == null) return;
      range.selectNodeContents(editable);
      range.collapse(false);
    }
  }, [autoFocus]);

  useEffect(() => {
    const editable = editableRef.current;

    // we cannot use the synthetic onBeforeInput handler because it does not give us an InputEvent
    // See https://github.com/facebook/react/issues/11211
    function onBeforeInput(e: InputEvent) {
      if (DISALLOWED_INPUT_TYPES.includes(e.inputType)) {
        e.preventDefault();
      }
    }

    editable?.addEventListener('beforeinput', onBeforeInput);
    return () => editable?.removeEventListener('beforeinput', onBeforeInput);
  }, []);

  return (
    <span
      ref={editableRef}
      contentEditable
      suppressContentEditableWarning
      className={styles.editable}
      // @ts-expect-error should change to `data-placeholder` since it's a custom attribute
      placeholder={placeholder}
      onPaste={(e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');

        const range = document.getSelection()?.getRangeAt(0);
        if (range == null) return;

        range.deleteContents();
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);
        range.selectNodeContents(textNode);
        range.collapse(false);
        const selection = document.getSelection();

        if (selection == null) return;

        selection.removeAllRanges();
        selection.addRange(range);

        handleChange();
      }}
      onInput={handleChange}
    >
      {defaultValueRef.current}
    </span>
  );
};

export default InPlaceEditable;
